import type { PickerRef } from '@rc-component/picker';
import dateFnsGenerateConfig from '@rc-component/picker/generate/dateFns';
import { DatePicker as AntdDatePicker } from 'antd';
import {
  endOfDay,
  endOfSecond,
  startOfDay,
  startOfSecond,
  subDays,
} from 'date-fns';
import { type ComponentProps, type Ref } from 'react';

const AiDatePicker: ReturnType<typeof AntdDatePicker.generatePicker<Date>> =
  AntdDatePicker.generatePicker<Date>(dateFnsGenerateConfig);

export const DatePicker = (
  props: ComponentProps<typeof AiDatePicker<Date>> & { ref?: Ref<PickerRef> },
) => {
  return <AiDatePicker {...props} />;
};

export type DatePickerProps = ComponentProps<typeof DatePicker>;

export const TimePicker = (
  props: ComponentProps<typeof AiDatePicker.TimePicker<Date>> & {
    ref?: Ref<PickerRef>;
  },
) => {
  return <AiDatePicker.TimePicker {...props}></AiDatePicker.TimePicker>;
};

export type TimePickerProps = ComponentProps<typeof TimePicker>;

export const DateRangePicker = (
  props: ComponentProps<typeof AiDatePicker.RangePicker> & {
    ref?: Ref<PickerRef>;
  },
) => {
  // 优化：showTime为true时，开始时间时分秒默认00:00:00，结束时间时分秒默认23:59:59
  let _props = props;
  if (_props.showTime === true) {
    _props = {
      ...props,
      showTime: {
        defaultValue: [startOfDay(Date.now()), endOfDay(Date.now())],
      },
    };
  }

  return (
    <AiDatePicker.RangePicker
      presets={COMMON_DATE_RANGE_PRESETS}
      {..._props}
      onChange={(dates, dateStrings) => {
        // RangePicker 的 value 不能为 undefined, 原先 dates?.map 会返回 undefined，导致报错
        if (dates == null) {
          props.onChange?.(dates, dateStrings);
        } else {
          props.onChange?.(
            dates.map((date, index) => {
              if (date == null) {
                return null;
              }

              if (_props.showTime) {
                if (index === 0) {
                  return startOfSecond(date);
                } else {
                  return endOfSecond(date);
                }
              } else {
                if (index === 0) {
                  return startOfDay(date);
                } else {
                  return endOfDay(date);
                }
              }
            }) as typeof dates,
            dateStrings,
          );
        }
      }}
    ></AiDatePicker.RangePicker>
  );
};

export type DateRangePickerProps = ComponentProps<typeof DateRangePicker>;

export type PresetDateValue = NonNullable<
  DateRangePickerProps['presets']
>[number]['value'];

export const COMMON_DATE_RANGE_PRESETS: NonNullable<
  DateRangePickerProps['presets']
> = [
  {
    label: '今天',
    value: () => [startOfDay(Date.now()), endOfDay(Date.now())],
  },
  {
    label: '昨天',
    value: () => [
      subDays(startOfDay(Date.now()), 1),
      subDays(endOfDay(Date.now()), 1),
    ],
  },
  {
    label: '前天',
    value: () => [
      subDays(startOfDay(Date.now()), 2),
      subDays(endOfDay(Date.now()), 2),
    ],
  },
  {
    label: '近3天',
    value: () => [subDays(startOfDay(Date.now()), 2), endOfDay(Date.now())],
  },
  {
    label: '近7天',
    value: () => [subDays(startOfDay(Date.now()), 6), endOfDay(Date.now())],
  },
  {
    label: '近15天',
    value: () => [subDays(startOfDay(Date.now()), 14), endOfDay(Date.now())],
  },
  {
    label: '近30天',
    value: () => [subDays(startOfDay(Date.now()), 29), endOfDay(Date.now())],
  },
  {
    label: '近90天',
    value: () => [subDays(startOfDay(Date.now()), 89), endOfDay(Date.now())],
  },
];

export type { PickerRef };
